/**  Arrow SVG imported:
 *   https://img.icons8.com/?size=100&id=39792&format=png&color=FFFFFF  **/

/***************/
/**  IMPORTS  **/
/***************/
import React from "react";

/***********************/
/**  AMPLIFY IMPORTS  **/
/***********************/

/****************************/
/**  LOCAL/ASSETS IMPORTS  **/
/****************************/
import LocationBar from "./LocationBar";
import VerticalStreet from "./VerticalStreet";
import ParkBar from "./ParkBar";
import { MyRow, 
         LeftBlock,
         RightBlock, 
         HorizontalStreet} from "../../styles/MapStyles";


const StreetBlock = ({formattedAddress, locationBarProps, filteredLocations, addresses, streetname, between, building}) => {
    
    // Helper function to remove duplicates based on building number
    const removeDuplicates = (addressArray) => {
        const seenBuildingNumbers = new Set();
        return addressArray?.filter((addressObj) => {
            const buildingNumber = parseInt(addressObj.address.split(" ")[0], 10);
            if (seenBuildingNumbers.has(buildingNumber)) {
                return false; // Skip duplicates
            }
            seenBuildingNumbers.add(buildingNumber);
            return true;
        });
    };

    // Remove duplicates from evens and odds
    const uniqueEvens = removeDuplicates(formattedAddress?.evens);
    const uniqueOdds = removeDuplicates(formattedAddress?.odds);

    const evensCount = formattedAddress?.evens?.length || 0;
    const oddsCount = formattedAddress?.odds?.length || 0;

    // Helper function to calculate horizontal count
    const calculateHorizontalCount = (addressArray) => {
        if (!Array.isArray(addressArray)) return 0; // Default to 0 if not an array
        return addressArray.reduce((count, addressObj) => {
            const buildingNumber = addressObj.address.split(" ")[0]; // Get building number
            const horizontalStreetText = between?.[buildingNumber];
            return horizontalStreetText ? count + 1 : count;
        }, 0);
    };

    // Calculate horizontal counts for evens and odds
    const horizontalCountEvens = calculateHorizontalCount(uniqueEvens);
    const horizontalCountOdds = calculateHorizontalCount(uniqueOdds);

    // Get the maximum horizontal count
    const maxHorizontalCount = Math.max(horizontalCountEvens, horizontalCountOdds);
    
    let evensMatchCount = 0;
    let evensLeftOvers = 0;
    if (uniqueEvens?.length > 0) {
        for (const item of uniqueEvens) {
            const buildingNumber = item.address.split(" ")[0];
            const newParks = {};
    
            for (const [key, value] of Object.entries(locationBarProps.parks)) {
                const tokens = key.split(" ");
                const firstToken = tokens[0].toLowerCase();
    
                if (firstToken === "before" || firstToken === "after") {
                    const newKey = tokens.slice(1).join(" ");
    
                    // Use an array to hold multiple values under the same key
                    if (!newParks[newKey]) {
                        newParks[newKey] = [];
                    }
                    newParks[newKey].push(value);
                }
            }
    
            if (newParks.hasOwnProperty(buildingNumber)) {
                evensMatchCount += newParks[buildingNumber].length;
            }
        }
    }
    if (uniqueEvens?.length < 1){
        for(const [key, value] of Object.entries(locationBarProps.parks)) {
            if(key === building){
                evensLeftOvers++;
            }
        }
    }

    let oddsMatchCount = 0;
    let oddsLeftOvers = 0;
    if (uniqueOdds?.length > 0) {
        for (const item of uniqueOdds) {
            const buildingNumber = item.address.split(" ")[0];
            const newParks = {};
    
            for (const [key, value] of Object.entries(locationBarProps.parks)) {
                const tokens = key.split(" ");
                const firstToken = tokens[0].toLowerCase();
    
                if (firstToken === "before" || firstToken === "after") {
                    const newKey = tokens.slice(1).join(" ");
    
                    // Use an array to hold multiple values under the same key
                    if (!newParks[newKey]) {
                        newParks[newKey] = [];
                    }
                    newParks[newKey].push(value);
                }
            }
    
            if (newParks.hasOwnProperty(buildingNumber)) {
                oddsMatchCount += newParks[buildingNumber].length;
            }
        }
    }

    if (uniqueOdds?.length < 1){
        for(const [key, value] of Object.entries(locationBarProps.parks)) {
            const incrementedBuilding = (Number(building) + 1).toString();
            if(key === incrementedBuilding){
                oddsLeftOvers++;
            }
        }
    }
    
    // Calculate the total height based on the max address count
    //                         location bars       horizontal streets           individual       not individual             location bars       horizontal streets           individual       not individual
    let maxHeight = Math.max(((evensCount * 80) + (maxHorizontalCount * 30) + ((evensLeftOvers + evensMatchCount) * 100)),((oddsCount * 80) + (maxHorizontalCount * 30) + 5 + ((oddsLeftOvers + oddsMatchCount) * 100)));

    const beforePark = {};
    const afterPark = {};

    for (const [key, value] of Object.entries(locationBarProps.parks)) {
        const tokens = key.split(" ");
        const firstToken = tokens[0];
        
        if (firstToken.toLowerCase() === "before") {
            const newKey = tokens.slice(1).join(" ");
            beforePark[newKey] = value;
        } else if (firstToken.toLowerCase() === "after") {
            const newKey = tokens.slice(1).join(" ");
            afterPark[newKey] = value;
        }
    }

    function countInSameHundreds(parks, building) {
        const buildingNum = parseInt(building, 10);
        const baseHundreds = Math.floor(buildingNum / 100) * 100;
      
        let evenCount = 0;
        let oddCount = 0;
      
        for (const key in parks) {
          const tokens = key.split(" ");
          const lastToken = tokens[tokens.length - 1];
          const num = parseInt(lastToken, 10);
      
          if (!isNaN(num) && num >= baseHundreds && num < baseHundreds + 100) {
            if (num % 2 === 0) {
              evenCount++;
            } else {
              oddCount++;
            }
          }
        }
      
        return { even: evenCount, odd: oddCount };
    }

    const parkPerBlockEvens = countInSameHundreds(locationBarProps.parks, building).even;
    const parkPerBlockOdds = countInSameHundreds(locationBarProps.parks, building).odd;

    const addressesEvens = (evensCount * 80) + 50 + (maxHorizontalCount * 30);
    // const remainingHeightEvens = maxHeight - addressesEvens;
    const remainingHeightEvens = (maxHeight - addressesEvens) / parkPerBlockEvens;

    const addressesOdds = (oddsCount * 80) + 50 + (maxHorizontalCount * 30);
    // const remainingHeightOdds = maxHeight - addressesOdds;
    const remainingHeightOdds = (maxHeight - addressesOdds) / parkPerBlockOdds;

    return (
        <>
        { locationBarProps.flipped  ? (
            <MyRow className="block">
            {/* LeftBlock: Odds */}
            <LeftBlock>
                {oddsLeftOvers > 0 && (
                    <MyRow>
                        <ParkBar 
                            parks={locationBarProps.parks[(Number(building) + 1).toString()]}
                            height={remainingHeightOdds} />
                    </MyRow>
                )}
                {uniqueOdds?.map((addressObj, index) => {
                    const buildingNumber = addressObj.address.split(" ")[0]; // Get building number
                    const horizontalStreetText = between?.[buildingNumber];
                    const beforeParkBar = beforePark?.[buildingNumber];
                    const afterParkBar = afterPark?.[buildingNumber];

                    return (
                        <React.Fragment key={`odds-${addressObj._id}`}>
                            
                            {beforeParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={beforePark[buildingNumber]}
                                        height={remainingHeightOdds} />
                                </MyRow>
                            )}
                            <MyRow>
                                <LocationBar
                                    data={{
                                        data: [{ formattedAddress: addressObj.address }],
                                        locs: filteredLocations, // Pass the appropriate data here
                                        addresses: addresses, // The full group of addresses
                                    }}
                                    {...locationBarProps}
                                    key={addressObj._id}
                                />
                            </MyRow>
                            {horizontalStreetText && ( // Only render HorizontalStreet if a match is found
                                <HorizontalStreet
                                    style={{
                                        letterSpacing: "0px",
                                        fontSize: "8px",
                                        width: "105%",
                                        marginRight: "-33px"
                                    }}
                                    key={`odds-street-${addressObj._id}`}>
                                    {horizontalStreetText}
                                </HorizontalStreet>
                            )}
                            {afterParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={afterPark[buildingNumber]}
                                        height={remainingHeightOdds} />
                                </MyRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </LeftBlock>

            {/* VerticalStreet */}
            <VerticalStreet streetname={streetname} maxHeight={maxHeight} />

            {/* LeftBlock: Evens */}
            <RightBlock>
                {evensLeftOvers > 0 && (
                    <MyRow>
                        <ParkBar 
                            parks={locationBarProps.parks[building]}
                            height={remainingHeightEvens} />
                    </MyRow>
                )}
                {uniqueEvens?.map((addressObj, index) => {
                    const buildingNumber = addressObj.address.split(" ")[0]; // Get building number
                    const horizontalStreetText = between?.[buildingNumber];
                    const beforeParkBar = beforePark?.[buildingNumber];
                    const afterParkBar = afterPark?.[buildingNumber];

                    return (
                        <React.Fragment key={`evens-${addressObj._id}`}>
                            
                            {beforeParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={beforePark[buildingNumber]}
                                        height={remainingHeightEvens} />
                                </MyRow>
                            )}
                            <MyRow style={{ position: "relative" }}>
                                <LocationBar
                                    data={{
                                        data: [{ formattedAddress: addressObj.address }],
                                        locs: filteredLocations, // Pass the appropriate data here
                                        addresses: addresses, // The full group of addresses
                                    }}
                                    {...locationBarProps}
                                    key={addressObj._id}
                                />
                            </MyRow>
                            {horizontalStreetText && ( // Only render HorizontalStreet if a match is found
                                <HorizontalStreet
                                    style={{
                                        marginLeft: "-55px",
                                        fontSize: "8px",
                                        width: "105%",
                                        position: "relative",
                                    }}
                                    key={`evens-street-${addressObj._id}`}>
                                    {horizontalStreetText}
                                </HorizontalStreet>
                            )}
                            {afterParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={afterPark[buildingNumber]}
                                        height={remainingHeightEvens} />
                                </MyRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </RightBlock>
        </MyRow>
        ) : (
            <MyRow className="block">
            {/* LeftBlock: Evens */}
            <LeftBlock>
                {evensLeftOvers > 0 && (
                    <MyRow>
                        <ParkBar 
                            parks={locationBarProps.parks[building]}
                            height={remainingHeightEvens} />
                    </MyRow>
                )}
                {uniqueEvens?.map((addressObj, index) => {
                    const buildingNumber = addressObj.address.split(" ")[0]; // Get building number
                    const horizontalStreetText = between?.[buildingNumber];
                    const beforeParkBar = beforePark?.[buildingNumber];
                    const afterParkBar = afterPark?.[buildingNumber];
                    
                    return (
                        <React.Fragment key={`evens-${addressObj._id}`}>
                            
                            {beforeParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={beforePark[buildingNumber]}
                                        height={remainingHeightEvens} />
                                </MyRow>
                            )}
                            <MyRow style={{ position: "relative" }}>
                                <LocationBar
                                    data={{
                                        data: [{ formattedAddress: addressObj.address }],
                                        locs: filteredLocations, // Pass the appropriate data here
                                        addresses: addresses, // The full group of addresses
                                    }}
                                    {...locationBarProps}
                                    key={addressObj._id}
                                />
                            </MyRow>
                            {horizontalStreetText && ( // Only render HorizontalStreet if a match is found
                                <HorizontalStreet
                                    style={{
                                        marginLeft: "-25px",
                                        fontSize: "8px",
                                        width: "105%",
                                        position: "relative",
                                        left: "33px"
                                    }}
                                    key={`evens-street-${addressObj._id}`}>
                                    {horizontalStreetText}
                                </HorizontalStreet>
                            )}
                            {afterParkBar && (
                                <MyRow style={{ position: "relative" }}>
                                    <ParkBar 
                                        parks={afterPark[buildingNumber]}
                                        height={remainingHeightEvens} />
                                </MyRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </LeftBlock>

            {/* VerticalStreet */}
            <VerticalStreet streetname={streetname} maxHeight={maxHeight} />

            {/* RightBlock: Odds */}
            <RightBlock >
                {oddsLeftOvers > 0 && (
                    <MyRow>
                        <ParkBar 
                            parks={locationBarProps.parks[(Number(building) + 1).toString()]}
                            height={remainingHeightOdds} />
                    </MyRow>
                )}
                {uniqueOdds?.map((addressObj, index) => {
                    const buildingNumber = addressObj.address.split(" ")[0]; // Get building number
                    const horizontalStreetText = between?.[buildingNumber];
                    const beforeParkBar = beforePark?.[buildingNumber];
                    const afterParkBar = afterPark?.[buildingNumber];
                    return (
                        <React.Fragment key={`odds-${addressObj._id}`}>
                            {beforeParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={beforePark[buildingNumber]}
                                        height={remainingHeightOdds} />
                                </MyRow>
                            )}
                            <MyRow>
                                <LocationBar
                                    data={{
                                        data: [{ formattedAddress: addressObj.address }],
                                        locs: filteredLocations, // Pass the appropriate data here
                                        addresses: addresses, // The full group of addresses
                                    }}
                                    {...locationBarProps}
                                    key={addressObj._id}
                                />
                            </MyRow>
                            {horizontalStreetText && ( // Only render HorizontalStreet if a match is found
                                <HorizontalStreet
                                    style={{
                                        letterSpacing: "0px",
                                        marginLeft: "-55px",
                                        fontSize: "8px",
                                        width: "105%",
                                    }}
                                    key={`odds-street-${addressObj._id}`}>
                                    {horizontalStreetText}
                                </HorizontalStreet>
                            )}
                            {afterParkBar && (
                                <MyRow>
                                    <ParkBar 
                                        parks={afterPark[buildingNumber]}
                                        height={remainingHeightOdds}/>
                                </MyRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </RightBlock>
        </MyRow>
        )}</>
        
    );
};

export default StreetBlock;